<template>
  <v-dialog v-model="open" max-width="400" @click:outside="$emit('close')" @keydown.enter.native="save">
    <v-card v-if="item" style="border-radius:14px; overflow:hidden;">

      <div class="modal-header">
        <v-icon color="#00BCD4" size="18" class="mr-2">mdi-package-variant-outline</v-icon>
        <span class="modal-title">{{ item.name }}</span>
      </div>

      <div class="modal-body" @keydown.enter="e => { e.target.blur(); $nextTick(save) }">
        <v-row dense>
          <v-col cols="6">
            <v-text-field
              ref="qtyInput"
              :value="parseFloat(editQty).toFixed(2)"
              label="Količina"
              type="number"
              min="0.001"
              step="0.001"
              outlined dense hide-details
              class="modal-input"
              @focus="$event.target.select()"
              @blur="v => editQty = parseFloat(v.target.value) || 1"
            />
          </v-col>
          <v-col v-if="!showVpc" cols="6">
            <v-text-field
              :value="parseFloat(editPrice).toFixed(2)"
              label="Cijena"
              type="number" min="0" step="0.01"
              outlined dense hide-details suffix="€"
              class="modal-input"
              readonly
            />
          </v-col>
        </v-row>

        <!-- MPC + VPC u istom redu — samo za VP blagajnu -->
        <v-row v-if="showVpc" dense class="mt-3">
          <v-col cols="6">
            <v-text-field
              :value="parseFloat(editPrice).toFixed(2)"
              label="MPC"
              type="number" min="0" step="0.01"
              outlined dense hide-details suffix="€"
              class="modal-input"
              readonly
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              :value="vpcDisplay"
              label="VPC"
              type="number" min="0" step="0.01"
              outlined dense hide-details suffix="€"
              class="modal-input"
              readonly
            />
          </v-col>
        </v-row>

        <!-- Popust % i apsolutni iznos -->
        <v-row dense class="mt-3">
          <v-col cols="6">
            <v-text-field
              :value="parseFloat(editDiscount).toFixed(2)"
              label="Popust %"
              type="number"
              min="0"
              max="100"
              step="0.01"
              outlined dense hide-details
              class="modal-input"
              suffix="%"
              @focus="$event.target.select()"
              @blur="v => onDiscountPctBlur(v.target.value)"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              :value="discountAmountDisplay"
              label="Popust iznos"
              type="number"
              min="0"
              step="0.01"
              outlined dense hide-details
              class="modal-input"
              suffix="€"
              @focus="$event.target.select()"
              @blur="v => onDiscountAmtBlur(v.target.value)"
            />
          </v-col>
          <v-col cols="12" class="mt-3">
            <v-text-field
              v-model="editKpdcode"
              label="KPD šifra (neobvezno)"
              outlined dense
              class="modal-input"
              placeholder="xx.xx.xx"
              :error-messages="kpdError"
              @input="onKpdInput"
            />
          </v-col>
        </v-row>
      </div>

      <div class="modal-footer">
        <button class="btn-remove" @click="$emit('remove', index)">
          <v-icon size="14" color="#E53935" class="mr-1">mdi-delete-outline</v-icon>
          Ukloni
        </button>
        <v-spacer />
        <button class="btn-cancel" @click="$emit('close')">Odustani</button>
        <button class="btn-save" @click="save">Spremi</button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ShopItemModal',
  props: {
    open: { type: Boolean, default: false },
    item: { type: Object, default: null },
    index: { type: Number, default: -1 },
    registerType: { type: String, default: 'MP' },
    wholesaleView: { type: Boolean, default: false }
  },
  data () {
    return { editQty: 1, editPrice: 0, editDiscount: 0, editKpdcode: '', kpdError: '' }
  },

  computed: {
    showVpc () { return this.wholesaleView },
    taxRate () { return this.item?.tax?.rate ?? 0.25 },
    vpcDisplay () {
      if (!this.editPrice) return (0).toFixed(2)
      return (this.editPrice / (1 + this.taxRate)).toFixed(2)
    },
    // Apsolutni iznos popusta u eurima (samo za GUI prikaz)
    discountAmountDisplay () {
      if (!this.editDiscount || !this.editPrice) return (0).toFixed(2)
      return (this.editPrice * this.editDiscount / 100).toFixed(2)
    }
  },
  watch: {
    open (val) {
      if (val) {
        setTimeout(() => {
          const input = this.$refs.qtyInput && this.$refs.qtyInput.$el.querySelector('input')
          if (input) { input.focus(); input.select() }
        }, 150)
      }
    },
    item: {
      immediate: true,
      handler (val) {
        if (!val) return
        this.editQty = val.quantity || 1
        this.editPrice = val.unitPrice ? val.unitPrice / 100 : 0
        this.editDiscount = val.discount?.rate ? val.discount.rate / 100 : 0
        this.editKpdcode = val.kpdcode || ''
        this.kpdError = ''
      }
    }
  },
  methods: {
    onDiscountPctBlur (val) {
      this.editDiscount = Math.min(100, Math.max(0, parseFloat(val) || 0))
    },
    onDiscountAmtBlur (val) {
      const amt = Math.min(this.editPrice || 0, Math.max(0, parseFloat(val) || 0))
      if (!this.editPrice || this.editPrice <= 0) return
      // Izračunaj postotak iz apsolutnog iznosa, zaokruži na 2 decimale
      this.editDiscount = parseFloat((amt / this.editPrice * 100).toFixed(2))
    },
    onKpdInput (val) {
      if (!val) { this.kpdError = ''; return }
      const digits = val.replace(/\D/g, '').slice(0, 6)
      let formatted = digits
      if (digits.length > 4) formatted = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4)
      else if (digits.length > 2) formatted = digits.slice(0, 2) + '.' + digits.slice(2)
      this.editKpdcode = formatted
      this.kpdError = formatted && !/^\d{2}\.\d{2}\.\d{2}$/.test(formatted) ? 'Format mora biti xx.xx.xx' : ''
    },
    save () {
      if (this.kpdError) return
      this.$emit('save', this.index, {
        quantity: this.editQty,
        unitPrice: Math.round(this.editPrice * 100),
        discount: this.editDiscount ? { type: 'RATE', rate: this.editDiscount } : null,
        kpdcode: this.editKpdcode
      })
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-header {
  display: flex; align-items: center;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #F1F5F9;
}
.modal-title {
  font-size: 14px; font-weight: 700; color: #1e293b;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  text-transform: capitalize;
}
.modal-body { padding: 16px 20px 8px; }

/* Input stil isti kao u expanded tablici */
.modal-input >>> .v-input__slot {
  border-radius: 8px !important;
  background: #F8FAFC !important;
  min-height: 40px !important;
}
.modal-input >>> fieldset { border-color: #E2E8F0 !important; border-radius: 8px !important; }
.modal-input >>> .v-input__slot:hover fieldset { border-color: #00BCD4 !important; }
.modal-input.v-input--is-focused >>> fieldset { border-color: #00BCD4 !important; border-width: 2px !important; }
.modal-input >>> input { font-size: 13px !important; }
.modal-input >>> input[type=number]::-webkit-outer-spin-button,
.modal-input >>> input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.modal-input >>> input[type=number] { -moz-appearance: textfield; }

.modal-footer {
  display: flex; align-items: center;
  gap: 14px; padding: 12px 20px 16px;
  border-top: 1px solid #F1F5F9;
}
.btn-remove {
  display: flex; align-items: center;
  background: transparent; border: none; cursor: pointer;
  font-size: 12px; color: #E53935; padding: 6px 8px;
  border-radius: 6px; transition: background 0.15s;
}
.btn-remove:hover { background: rgba(229,57,53,0.08); }
.btn-cancel {
  padding: 9px 16px; background: #F8FAFC;
  border: 1.5px solid #E2E8F0; border-radius: 8px;
  font-size: 12px; font-weight: 600; color: #64748B;
  cursor: pointer; transition: all 0.15s;
}
.btn-cancel:hover { background: #F1F5F9; border-color: #CBD5E1; }
.btn-save {
  padding: 9px 18px;
  background-color: #00BCD4 !important; background: #00BCD4 !important;
  color: #fff !important; border-radius: 8px; border: none !important;
  font-size: 12px; font-weight: 700; cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,188,212,0.3);
  transition: background-color 0.15s;
}
.btn-save:hover { background-color: #0097A7 !important; background: #0097A7 !important; }
</style>
